// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useCarga.ts
// ════════════════════════════════════════════════════════════════════════════

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cargaService, type FiltroCargas } from '../services/cargaService';
import type { ListaFiltros } from '../services/listaFiltros';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { IniciarCargaRequest, AtualizarCargaRequest, RetornarCargaRequest } from '../types';

interface UseCargaParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período, filtros extras e ordem — aplicados no servidor. */
  filtros?: FiltroCargas | ListaFiltros;
}

export const useCarga = ({ pagina = 1, tamanhoPagina = 20, filtros }: UseCargaParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['carga', pagina, tamanhoPagina, filtros],
    queryFn: () => cargaService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: keepPreviousData, // a tabela não pisca a cada letra da busca
  });

  // KPIs da consulta inteira (o status não entra: os cards mostram a divisão por status).
  const resumo = useQuery({
    queryKey: ['carga', 'resumo', filtros],
    queryFn: () => cargaService.getResumo(filtros as FiltroCargas),
    placeholderData: keepPreviousData,
  });

  const cargas       = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const resumoLista = paginado?.resumo; // KPIs v118 (vêm na listagem)

  const iniciarMutation = useMutation({
    mutationFn: (request: IniciarCargaRequest) => cargaService.iniciar(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carga'] });
      toast.success('Carga iniciada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarCargaRequest }) =>
      cargaService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carga'] });
      toast.success('Carga atualizada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const retornoMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: RetornarCargaRequest }) =>
      cargaService.registrarRetorno(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carga'] });
      toast.success('Retorno da carga registrado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => cargaService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carga'] });
      toast.success('Carga excluída com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    resumo: resumo.data,
    cargas,
    total,
    totalPaginas,
    resumoLista,
    isLoading,
    isFetching,
    iniciarCarga: iniciarMutation.mutate,
    updateCarga: updateMutation.mutate,
    registrarRetorno: retornoMutation.mutate,
    deleteCarga: deleteMutation.mutate,
    isCreating: iniciarMutation.isPending,
  };
};
